import logoImg from "@/assets/images/logos/logo.png";
import { AgreementLinks } from "@/components/common/AgreementLinks";
import { useScrollLock } from "@/hooks/useScrollLock";
import { loginWithEmail } from "@/services/api/auth";
import { getUserProfileApi } from "@/services/api/profile";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { App, Button, Checkbox, Form, Grid, Input, Modal } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LoginModal.css";

type LoginModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (payload: {
    email: string;
    password: string;
  }) => Promise<void> | void;
  source?: "contact" | "feedback" | "beta"; // 登录来源：联系表单、意见反馈或加入内测联盟
};

// 色彩常量 - 用于粒子背景等动态场景
const colors = {
  primary: "#21C4B8",
  primaryDark: "#4BA67A",
  secondary: "#20a39e",
  textMuted: "#718096",
};

// 粒子背景组件
const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 设置画布尺寸
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // 粒子系统
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
    }> = [];

    // 创建粒子
    for (let i = 0; i < 30; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        color: Math.random() > 0.5 ? colors.primary : colors.secondary,
      });
    }

    // 动画循环
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      particles.forEach((particle, index) => {
        // 更新位置
        particle.x += particle.vx;
        particle.y += particle.vy;

        // 边界检测
        if (particle.x < 0 || particle.x > canvas.offsetWidth)
          particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.offsetHeight)
          particle.vy *= -1;

        // 绘制粒子
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle =
          particle.color +
          Math.floor(particle.opacity * 255)
            .toString(16)
            .padStart(2, "0");
        ctx.fill();

        // 连接线
        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 100) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.strokeStyle =
                colors.primary +
                Math.floor((1 - distance / 100) * 50)
                  .toString(16)
                  .padStart(2, "0");
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return <canvas ref={canvasRef} className="login-modal-particles" />;
};

export const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onSubmit,
  source,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  // 防止移动端滚动穿透
  useScrollLock(open);

  // 协议提示动画状态
  const [showAgreementAlert, setShowAgreementAlert] = useState(false);
  const agreementAlertRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 使用状态来跟踪表单字段值
  const [formValues, setFormValues] = useState({ email: "", password: "" });

  const canSubmit = useMemo(() => {
    return Boolean(
      formValues.email &&
      formValues.password &&
      formValues.password.length >= 6 &&
      agree
    );
  }, [formValues, agree]);

  const canTriggerLogin = useMemo(() => {
    return Boolean(
      formValues.email && formValues.password && formValues.password.length >= 6
    );
  }, [formValues]);

  // 监听表单字段变化
  const handleFormChange = () => {
    const email = form.getFieldValue("email") || "";
    const password = form.getFieldValue("password") || "";
    setFormValues({ email, password });
  };

  const handleOk = async () => {
    // 如果条件满足但未同意协议，触发提示动画
    if (!agree && canTriggerLogin) {
      triggerAgreementAlert();
      return;
    }

    try {
      const values = await form.validateFields();
      console.log("[LoginModal] validateFields values:", values);

      setLoading(true);

      const payload = {
        email: values.email as string,
        password: values.password as string,
      };
      console.log("[LoginModal] calling loginWithEmail with payload:", payload);
      const res = await loginWithEmail(payload);
      console.log("[LoginModal] loginWithEmail response:", res);

      // 兼容真实接口(axios响应: res.data.data.token) 与 mock 返回(res.data.token)
      const token =
        (res as any)?.data?.token || (res as any)?.data?.data?.token;
      if (token) {
        console.log("[LoginModal] setting token:", token);
        localStorage.setItem("token", token as string);

        // 同步 token 到 store
        const { setToken, setUser } = useAuthStore.getState();
        setToken(token);

        message.success(t("auth.common.message_login_success"));
        if (onSubmit) {
          console.log("[LoginModal] notifying parent via onSubmit:", payload);
          await onSubmit(payload);
        }

        // 保存user_id
        try {
          const user_id =
            (res as any)?.data?.user?.id ?? (res as any)?.data?.data?.user?.id;
          if (user_id) {
            localStorage.setItem("user_id", user_id);
          }
        } catch {}

        // 先关闭登录弹窗
        onClose();
        // 延迟一点时间再打开对应的弹窗，确保登录弹窗完全关闭
        setTimeout(() => {
          try {
            console.log("[LoginModal] source:", source);
            if (source === "feedback") {
              window.dispatchEvent(new CustomEvent("feedback:open"));
            } else if (source === "beta") {
              window.dispatchEvent(new CustomEvent("contact-us:open"));
            } else if (source === "contact") {
              // 对于内测联盟页面，不需要重新打开，因为它已经在页面上
              console.log("[LoginModal] 内测联盟页面保持打开状态");
            }
          } catch {}
        }, 300);

        // 登录成功后，立即获取用户信息
        const currentUserId = localStorage.getItem("user_id");
        if (currentUserId) {
          try {
            const profileResponse = await getUserProfileApi({
              user_id: currentUserId,
            });
            console.log("[LoginModal] 登录后获取用户信息:", profileResponse);

            if (profileResponse?.code === 200 && profileResponse.data) {
              localStorage.setItem(
                "audit_status",
                profileResponse.data.beta_data?.audit_status?.toString() || ""
              );
              setUser(profileResponse.data);
            }
          } catch (error) {
            console.error("[LoginModal] 获取用户信息失败:", error);
          }
        }
      }

      // 登录成功后清空所有表单数据
      form.resetFields();
      setFormValues({ email: "", password: "" });
      setAgree(false);
    } catch (error: any) {
      message.error(error?.message || t("auth.common.message_login_failed"));
    } finally {
      setLoading(false);
    }
  };

  // 触发协议提示动画
  const triggerAgreementAlert = () => {
    if (!agree && canTriggerLogin) {
      setShowAgreementAlert(true);
      message.warning(t("login.tip_agree_first"));

      if (agreementAlertRef.current) {
        clearTimeout(agreementAlertRef.current);
      }

      agreementAlertRef.current = setTimeout(() => {
        setShowAgreementAlert(false);
      }, 2000);
    }
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (agreementAlertRef.current) {
        clearTimeout(agreementAlertRef.current);
        agreementAlertRef.current = null;
      }
    };
  }, []);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? 360 : 560}
      styles={{
        content: {
          borderRadius: "24px",
          padding: "0",
          background: "#ffffff",
          backdropFilter: "blur(20px) saturate(180%)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow:
            "0 32px 64px rgba(0, 0, 0, 0.15), 0 16px 32px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
          overflow: "hidden",
          position: "relative",
        },
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(12px) saturate(180%)",
        },
      }}
      destroyOnHidden
      centered
      rootClassName="login-modal-custom"
    >
      {/* 背景装饰 */}
      <div className="login-modal-bg-decoration" />

      {/* 粒子背景 */}
      <ParticleBackground />

      {/* 主要内容 */}
      <div className={`login-modal-content ${isMobile ? "mobile" : ""}`}>
        {/* 头部区域 */}
        <div className="login-modal-header">
          <div className="login-modal-logo">
            <img src={logoImg} alt="GenOptima" />
          </div>
          <div className="login-modal-subtitle">{t("login.start_journey")}</div>
        </div>

        {/* 表单区域 */}
        <div className="login-modal-form">
          <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
            {/* 邮箱输入 */}
            <Form.Item
              name="email"
              label={
                <span className="login-modal-label">
                  {t("auth.common.label_corporate_email")}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: t("auth.common.validation_email_required"),
                },
                {
                  type: "email",
                  message: t("auth.common.validation_email_invalid"),
                },
              ]}
              style={{ marginBottom: "24px" }}
            >
              <Input
                prefix={<MailOutlined style={{ color: colors.textMuted }} />}
                placeholder={t("auth.common.placeholder_email")}
                size="large"
                className="login-input"
              />
            </Form.Item>

            {/* 密码输入 */}
            <Form.Item
              name="password"
              label={
                <div className="login-modal-password-label">
                  <span className="login-modal-label">
                    {t("auth.common.label_password")}
                  </span>
                </div>
              }
              rules={[
                {
                  required: true,
                  message: t("auth.common.validation_password_required"),
                },
                { min: 6, message: t("auth.common.validation_password_min") },
              ]}
              style={{ marginBottom: "36px" }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: colors.textMuted }} />}
                placeholder={t("auth.common.placeholder_password")}
                size="large"
                className="login-input"
                iconRender={(visible) =>
                  visible ? (
                    <EyeOutlined style={{ color: colors.textMuted }} />
                  ) : (
                    <EyeInvisibleOutlined style={{ color: colors.textMuted }} />
                  )
                }
              />
            </Form.Item>

            {/* 协议区域 */}
            <div
              className={`login-modal-agreement custom-checkbox ${showAgreementAlert ? "alert" : ""}`}
            >
              <div className="login-modal-agreement-inner">
                <Checkbox
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  style={{ transform: "scale(1.2)", flexShrink: 0 }}
                />
                <div className="login-modal-agreement-text">
                  <AgreementLinks
                    prefixText={t("login.agree_text")}
                    showCheckbox={false}
                    colors={{
                      primary: colors.primary,
                      primaryDark: colors.primaryDark,
                    }}
                    style={{ lineHeight: "1.6", fontSize: "14px" }}
                  />
                </div>
              </div>
            </div>

            {/* 登录按钮 */}
            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              className="login-modal-btn"
              style={{ color: "#fff" }}
              disabled={!canTriggerLogin}
              onClick={handleOk}
            >
              {t("auth.login.btn_login")}
            </Button>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default LoginModal;
